/**
 * Settings Hooks
 * React Query hooks for settings operations
 */

import type { ApiError } from "@shared-types/api.types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { toast } from "sonner"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import * as userService from "@/features/profile/services/user.service"
import i18n from "@/language"
import * as settingsService from "../services/settings.service"

/**
 * Change Password Hook
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) =>
      settingsService.changePassword(oldPassword, newPassword),
    onSuccess: data => {
      toast.success(data.message)
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to change password"
      toast.error(message)
    },
  })
}

/**
 * Set Password Hook (for OAuth users without a password)
 */
export function useSetPassword() {
  const { refreshUserProfile } = useRefreshUserProfile()

  return useMutation({
    mutationFn: (newPassword: string) => settingsService.setPassword(newPassword),
    onSuccess: async data => {
      try {
        await refreshUserProfile()
      } catch {
        // Profile refreshed even if refresh fails
      }
      toast.success(data.message)
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to set password"
      toast.error(message)
    },
  })
}

/**
 * Get Notification Settings Hook
 */
export function useNotificationSettings() {
  return useQuery({
    queryKey: ["notification-settings"],
    queryFn: settingsService.getNotificationSettings,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Update Notification Settings Hook
 */
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: settingsService.updateNotificationSettings,
    onSuccess: data => {
      queryClient.setQueryData(["notification-settings"], data)
      toast.success("Notification preferences saved")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to update notification preferences"
      toast.error(message)
    },
  })
}

/**
 * Update User Settings Hook (language, privacy toggles)
 */
export function useUpdateUserSettings() {
  const { refreshUserProfile } = useRefreshUserProfile()

  return useMutation({
    mutationFn: userService.updatePermissionSettings,
    onSuccess: async () => {
      try {
        await refreshUserProfile()
      } catch {
        // Settings saved even if refresh fails
      }
      toast.success("Settings saved")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to save settings"
      toast.error(message)
    },
  })
}

/**
 * Update Profile Hook (timezone, phone, email)
 */
export function useUpdateSettingsProfile() {
  const { refreshUserProfile } = useRefreshUserProfile()

  return useMutation({
    mutationFn: userService.updateProfile,
    onSuccess: async () => {
      try {
        await refreshUserProfile()
      } catch {
        // Profile updated even if refresh fails
      }
      toast.success("Profile updated")
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to update profile"
      toast.error(message)
    },
  })
}

/**
 * Delete Account Hook
 */
export function useDeleteAccount() {
  const logout = useAuthStore(state => state.logout)

  return useMutation({
    mutationFn: (password: string) => settingsService.deleteAccount(password),
    onSuccess: async () => {
      toast.success("Account deleted successfully")
      await logout()
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to delete account"
      toast.error(message)
    },
  })
}

// ============================================================================
// Subscription Hooks
// ============================================================================

/**
 * Get Subscription Hook
 * Fetches current user's subscription status
 */
export function useSubscription() {
  return useQuery({
    queryKey: ["subscription"],
    queryFn: () => settingsService.getSubscription(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useActiveSubscriptionPlan() {
  return useQuery({
    queryKey: ["subscription", "active-plan"],
    queryFn: () => settingsService.getActiveSubscriptionPlan(),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Create Subscription Checkout Hook
 * Creates a Stripe checkout session for new subscription
 */
export function useCreateSubscriptionCheckout() {
  return useMutation({
    mutationFn: ({ successUrl, cancelUrl }: { successUrl: string; cancelUrl: string }) =>
      settingsService.createSubscriptionCheckout(successUrl, cancelUrl),
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to create checkout session"
      toast.error(message)
    },
  })
}

/**
 * Cancel Subscription Hook
 * Cancels subscription at end of billing period
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => settingsService.cancelSubscription(),
    onSuccess: () => {
      toast.success(i18n.t("settings:subscription.cancelScheduled"))
      queryClient.invalidateQueries({ queryKey: ["subscription"] })
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to cancel subscription"
      toast.error(message)
    },
  })
}

/**
 * Create Payment Update Session Hook
 * Creates Stripe Billing Portal session for payment method update
 */
export function useCreatePaymentUpdateSession() {
  return useMutation({
    mutationFn: (returnUrl: string) => settingsService.createPaymentUpdateSession(returnUrl),
    onError: (error: unknown) => {
      const err = error as AxiosError<ApiError>
      const message = err.response?.data?.message || "Failed to create payment session"
      toast.error(message)
    },
  })
}
